// src/libs/hooks/use-prevent-page-leave/index.ts

'use client';

import { useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { doNothing } from '@/utils/function';

interface Props {
  isDirty: boolean;
  message?: string;
  onLeave?: () => void;
}

/**
 * App Router에서 컨텐츠 수정 중 페이지 이탈 방지 훅
 * @param isDirty - 수정 상태 여부
 * @param onLeave - 페이지 이탈 시 실행할 콜백 (선택사항)
 */
export const usePreventPageLeave = (props: Props) => {
  const router = useRouter();
  const isConfirmed = useRef(false);
  const {isDirty, message = '저장하지 않은 변경사항이 있습니다. 정말 나가시겠습니까?', onLeave = doNothing} = props;

  // 브라우저 탭 닫기/새로고침 감지
  const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
    if (isDirty && !isConfirmed.current) {
      e.preventDefault();
      e.returnValue = '';
      return '';
    }
  }, [isDirty]);

  // 라우터 push 메서드 오버라이드
  const confirmNavigation = useCallback(() => {
    if (!isDirty) return true;

    const confirmed = window.confirm(message);

    if (confirmed) {
      isConfirmed.current = true;
      onLeave?.();
    }
    return confirmed;
  }, [isDirty, message, onLeave]);

  // 라우터 네비게이션 메서드 오버라이드
  useEffect(() => {
    if (!isDirty) return;

    const originalPush = router.push;
    const originalReplace = router.replace;
    const originalBack = router.back;

    // push 메서드 오버라이드
    router.push = function(href: string) {
      if (isConfirmed.current || !isDirty) {
        isConfirmed.current = false;
        return originalPush.call(null, href);
      }

      if (window.confirm(message)) {
        isConfirmed.current = false;
        onLeave?.();
        return originalPush.call(null, href);
      }
    } as any;

    // replace 메서드 오버라이드
    router.replace = function(href: string) {
      if (isConfirmed.current || !isDirty) {
        isConfirmed.current = false;
        return originalReplace.call(null, href);
      }

      if (window.confirm(message)) {
        isConfirmed.current = false;
        onLeave?.();
        return originalReplace.call(null, href);
      }
    } as any;

    // back 메서드 오버라이드
    router.back = function() {
      if (isConfirmed.current || !isDirty) {
        isConfirmed.current = false;
        return originalBack.call(null);
      }

      if (window.confirm(message)) {
        isConfirmed.current = false;
        onLeave?.();
        return originalBack.call(null);
      }
    } as any;

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      // 원본 메서드 복원
      router.push = originalPush;
      router.replace = originalReplace;
      router.back = originalBack;
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, router, handleBeforeUnload, onLeave, message]);

  return { confirmNavigation };
};